import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AlertScrollArea from "./AlertScrollArea";

export default function AlertFilterDropdown({ value, options, onChange, ariaLabel, className = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const selectedOption = options.find((option) => String(option.value) === String(value)) || options[0];
  const optionLabelsKey = options.map((option) => String(option.label || "")).join("\u0000");

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuHeight = Math.min((options.length * 40) + 20, 280);
    const needsScroll = ((options.length * 36) + 20) > 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 12 && rect.top > spaceBelow;
    const computedStyle = window.getComputedStyle(rootRef.current);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    let longestLabelWidth = 0;
    if (context) {
      context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
      optionLabelsKey.split("\u0000").forEach((label) => {
        longestLabelWidth = Math.max(longestLabelWidth, context.measureText(label).width);
      });
    }
    const contentWidth = Math.ceil(longestLabelWidth) + 42 + (needsScroll ? 21 : 0);
    const menuWidth = Math.min(Math.max(rect.width, contentWidth), window.innerWidth - 16);
    setMenuPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8)),
      top: openUpward ? Math.max(8, rect.top - menuHeight - 12) : rect.bottom + 12,
      width: menuWidth,
    });
  }, [optionLabelsKey, options.length]);

  useLayoutEffect(() => {
    if (open) updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <div ref={rootRef} className={`crm-alert-dropdown ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""} ${className}`.trim()}>
      <button type="button" className="crm-alert-dropdown__toggle" aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} disabled={disabled} onClick={() => setOpen((current) => !current)}>
        <span>{selectedOption?.label || ""}</span>
        <span className="crm-alert-dropdown__caret" aria-hidden="true" />
      </button>
      {open && !disabled && menuPosition ? createPortal(
        <div ref={menuRef} className="crm-alert-dropdown__menu" role="listbox" aria-label={ariaLabel} style={menuPosition}>
          <AlertScrollArea viewportClassName="crm-alert-dropdown__scroll">
            {options.map((option) => {
              const selected = String(option.value) === String(value);
              return (
                <button
                  key={`${option.value}-${option.label}`}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`crm-alert-dropdown__option ${selected ? "is-selected" : ""}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </AlertScrollArea>
        </div>,
        document.body
      ) : null}
    </div>
  );
}
